import React from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"
import { ActiniumLogo } from "../actinium-logo"

const sizeMap = {
  xs: { box: 36, mark: 22, ring: 2.5, dot: 5, gap: 5 },
  sm: { box: 48, mark: 30, ring: 2.5, dot: 6, gap: 6 },
  md: { box: 64, mark: 40, ring: 3, dot: 7, gap: 7 },
  lg: { box: 88, mark: 56, ring: 3.5, dot: 8, gap: 8 },
  xl: { box: 120, mark: 76, ring: 4, dot: 10, gap: 10 },
}

const DOT_COLORS = ["#6366F1", "#F59E0B", "#10B981"]

const labelVariants = cva("font-medium text-muted-foreground", {
  variants: {
    size: {
      xs: "text-[11px]",
      sm: "text-xs",
      md: "text-sm",
      lg: "text-sm",
      xl: "text-base",
    },
  },
  defaultVariants: { size: "md" },
})

function BlinkDots({ dim }) {
  return (
    <span className="inline-flex items-center" style={{ gap: Math.max(4, dim.gap * 0.65) }} aria-hidden="true">
      {DOT_COLORS.map((color, i) => (
        <span
          key={color}
          className="ab-loader-blink-dot rounded-full"
          style={{
            width: dim.dot,
            height: dim.dot,
            backgroundColor: color,
            animationDelay: `${i * 0.4}s`,
          }}
        />
      ))}
    </span>
  )
}

function BrandLoader({ dim }) {
  return (
    <span className="inline-flex flex-col items-center" style={{ gap: dim.gap }}>
      <span
        className="relative inline-flex items-center justify-center"
        style={{ width: dim.box, height: dim.box }}
      >
        <span
          className="ab-loader-spin absolute inset-0 rounded-full border-solid"
          style={{
            borderWidth: dim.ring,
            borderColor: "rgba(99, 102, 241, 0.18)",
            borderTopColor: "#6366F1",
            borderRightColor: "rgba(245, 158, 11, 0.55)",
          }}
          aria-hidden="true"
        />
        <span
          className="relative z-[1] inline-flex overflow-hidden rounded-[22%] shadow-sm"
          style={{ width: dim.mark, height: dim.mark }}
          aria-hidden="true"
        >
          <ActiniumLogo size={dim.mark} title={null} className="h-full w-full" />
        </span>
      </span>
      <BlinkDots dim={dim} />
    </span>
  )
}

/**
 * Actinium Book branded loader:
 * A-shaped book mark, rotating ring, and three colored dots blinking in sequence.
 *
 * @param {"xs"|"sm"|"md"|"lg"|"xl"} [size="md"]
 * @param {string} [label] Optional status text
 * @param {"inline"|"stack"} [layout="stack"] Label placement
 * @param {boolean} [fullscreen=false]
 */
export function ActiniumLoader({
  size = "md",
  label,
  layout = "stack",
  fullscreen = false,
  className,
  ...props
}) {
  const dim = sizeMap[size] || sizeMap.md

  const content = (
    <span
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        "inline-flex text-foreground",
        layout === "inline" ? "flex-row items-center gap-3" : "flex-col items-center gap-2.5",
        className,
      )}
      {...props}
    >
      <BrandLoader dim={dim} />
      {label ? <span className={labelVariants({ size })}>{label}</span> : null}
      <span className="sr-only">{label || "Loading"}</span>
    </span>
  )

  if (!fullscreen) return content

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/75 backdrop-blur-[2px]">
      {content}
    </div>
  )
}

export const ACTINIUM_LOADER_SIZES = Object.keys(sizeMap)
export const ACTINIUM_LOADER_VARIANTS = ["default"]
